import { z } from 'zod'
import { Model } from '../../_kernel/architecture'
import { PaginationStatus } from '../../_kernel/PaginationStatus'
import { ListOfProperty } from './ListOfProperty'
import type { Property } from './Property'

export const ListOfPaginatedPropertyValidation = z.object({
  paginationStatus: z.instanceof(PaginationStatus, {
    message: '[ListOfPaginatedProperty] paginationStatus is required',
  }),
  properties: z.instanceof(ListOfProperty, {
    message: '[ListOfPaginatedProperty] list of property is required',
  }),
})

export class ListOfPaginatedProperty extends Model {
  static create({
    paginationStatus,
    properties,
  }: z.infer<typeof ListOfPaginatedPropertyValidation>) {
    ListOfPaginatedPropertyValidation.parse({ paginationStatus, properties })
    return new ListOfPaginatedProperty(paginationStatus, properties)
  }

  static empty() {
    return new ListOfPaginatedProperty(PaginationStatus.empty(), ListOfProperty.empty())
  }

  static fromJSON(json: ReturnType<ListOfPaginatedProperty['toJSON']>) {
    return ListOfPaginatedProperty.create({
      paginationStatus: PaginationStatus.fromJSON(json.pagination),
      properties: ListOfProperty.fromJSON({ json: json.properties }),
    })
  }

  static fromJSONOrEmpty(json: ReturnType<ListOfPaginatedProperty['toJSON']> | null) {
    try {
      if (!json) return ListOfPaginatedProperty.empty()
      return ListOfPaginatedProperty.create({
        paginationStatus: PaginationStatus.fromJSON(json.pagination),
        properties: ListOfProperty.fromJSON({ json: json.properties }),
      })
    } catch {
      return ListOfPaginatedProperty.empty()
    }
  }

  constructor(
    private readonly _paginationStatus: PaginationStatus,
    private readonly _properties: ListOfProperty
  ) {
    super()
  }

  get paginationStatus() { return this._paginationStatus } // prettier-ignore
  get properties() { return this._properties } // prettier-ignore

  map<T>(fn: (property: Property) => T) {
    return this._properties.map(fn)
  }

  isEmpty() {
    return this.properties.isEmpty() && this.paginationStatus.isEmpty()
  }

  hasProperties() {
    return !this.properties.isEmpty()
  }

  hasMorePages() {
    return !this._paginationStatus.isLastPage()
  }

  nextPage() {
    return this._paginationStatus.nextPage()
  }

  shiftProperties({ properties }: { properties: ListOfPaginatedProperty }) {
    const currentJSON = this.toJSON()
    const previusJSON = properties.toJSON()

    return ListOfPaginatedProperty.fromJSON({
      ...currentJSON,
      properties: {
        list: [...previusJSON.properties.list, ...currentJSON.properties.list],
      },
    })
  }

  getAllPropertyIds(): string[] {
    return this._properties.getAllIds()
  }

  toJSON() {
    return { pagination: this._paginationStatus.toJSON(), properties: this._properties.toJSON() }
  }
}
